import React from 'react';
import { RATE_HOUR, RATE_PT, calc, fmtChf, fmtChfTight, fmtNum, fmtPct } from './calc.js';
import { Banner, Btn, Field, KiBanner, OptCard, Pill, Slider } from './components.jsx';
import { CFG } from './config.js';
import { t } from './i18n.js';
const { useState } = React;

// ----------------------------------------------------------------------------
// Cost line chart — total per year + per-category lines, with time axis
// ----------------------------------------------------------------------------
const LineChart = ({ years }) => {
  const W = 760, H = 320;
  const pad = { l: 70, r: 20, t: 24, b: 44 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const series = [
    { key: 'total', label: t('p3.table_total_per_year'),     color: 'var(--primary-50)',   width: 2.5 },
    { key: 'lic',   label: t('p3.table_pos.lic'),            color: 'var(--info-50)',      width: 1.5 },
    { key: 'serv',  label: t('p3.table_pos.serv'),           color: 'var(--secondary-50)', width: 1.5 },
    { key: 'host',  label: t('p3.table_pos.host'),           color: 'var(--tertiary-50)',  width: 1.5 },
    { key: 'grow',  label: t('p3.table_pos.grow'),           color: 'var(--warning-50)',   width: 1.5 },
  ];

  const maxY = Math.max(...years.map(y => y.total)) || 1;
  const niceMax = (() => {
    const exp = Math.pow(10, Math.floor(Math.log10(maxY)));
    const m = maxY / exp;
    const nice = m > 5 ? 10 : m > 2 ? 5 : m > 1 ? 2 : 1;
    return nice * exp;
  })();
  const ticks = 5;
  const xFor = (i) => pad.l + (i / Math.max(1, years.length - 1)) * innerW;
  const yFor = (v) => pad.t + innerH - (v / niceMax) * innerH;

  const fmtAxis = (v) => {
    if (v >= 1000000) return (v / 1000000).toFixed(v >= 10000000 ? 0 : 1).replace(/\.0$/,'') + 'M';
    if (v >= 1000) return Math.round(v / 1000) + 'k';
    return String(v);
  };

  const buildPath = (key) => years.map((y, i) => {
    const x = xFor(i), yv = yFor(y[key] || 0);
    return (i === 0 ? 'M' : 'L') + x.toFixed(2) + ',' + yv.toFixed(2);
  }).join(' ');

  const totalAreaPath = (() => {
    let d = '';
    years.forEach((y, i) => {
      d += (i === 0 ? 'M' : 'L') + xFor(i).toFixed(2) + ',' + yFor(y.total).toFixed(2) + ' ';
    });
    d += 'L ' + xFor(years.length - 1).toFixed(2) + ',' + yFor(0).toFixed(2) + ' ';
    d += 'L ' + xFor(0).toFixed(2) + ',' + yFor(0).toFixed(2) + ' Z';
    return d;
  })();

  return (
    <div className="linechart">
      <svg viewBox={`0 0 ${W} ${H}`} className="linechart__svg" role="img" aria-label={t('p3.chart_view')}>
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const v = (niceMax * (ticks - i)) / ticks;
          const y = pad.t + (innerH * i) / ticks;
          return (
            <g key={i}>
              <line x1={pad.l} x2={W - pad.r} y1={y} y2={y} className="linechart__grid"/>
              <text x={pad.l - 10} y={y + 4} className="linechart__tick linechart__tick--y">
                CHF {fmtAxis(v)}
              </text>
            </g>
          );
        })}

        <path d={totalAreaPath} className="linechart__area"/>

        {series.map(s => (
          <path key={s.key} d={buildPath(s.key)} fill="none" stroke={s.color}
                strokeWidth={s.width} strokeLinejoin="round" strokeLinecap="round"
                className={s.key === 'total' ? 'linechart__line linechart__line--total' : 'linechart__line'}/>
        ))}

        {years.map((y, i) => (
          <g key={y.year}>
            <circle cx={xFor(i)} cy={yFor(y.total)} r={4.5}
              fill="var(--global-background)" stroke="var(--primary-50)" strokeWidth={2}/>
            <text x={xFor(i)} y={yFor(y.total) - 12} className="linechart__pointlabel" textAnchor="middle">
              {fmtChfTight(y.total)}
            </text>
          </g>
        ))}

        {years.map((y, i) => (
          <text key={y.year} x={xFor(i)} y={H - pad.b + 22} className="linechart__tick linechart__tick--x" textAnchor="middle">
            {t('p3.table_year', { year: y.year })}{y.year === 0 ? ' · ' + t('p3.table_year_build') : ''}
          </text>
        ))}

        <line x1={pad.l} x2={W - pad.r} y1={pad.t + innerH} y2={pad.t + innerH} className="linechart__axis"/>
      </svg>

      <div className="linechart__legend">
        {series.map(s => (
          <span key={s.key} className="linechart__legend-item">
            <span className="linechart__legend-swatch"
              style={{ background: s.color, height: s.key === 'total' ? 3 : 2 }}/>
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const Page3 = ({ state, setState, openDrawer, calc }) => {
  const { p3 } = state;
  const setP3 = (patch) => setState(s => ({ ...s, p3: { ...s.p3, ...patch } }));
  const r = calc(state);

  const [tableMode, setTableMode] = useState('table');

  const recommended = Number.isFinite(r.recommendedBudget) ? r.recommendedBudget : 250000;
  const sliderMin = Math.max(50000, recommended * 0.4);
  const sliderMax = Math.max(recommended * 1.8, 800000);
  const currentBudget = Number.isFinite(p3.budget) ? p3.budget : recommended;

  const fmtKchf = (v) => 'CHF ' + Math.round(v / 1000).toLocaleString('de-CH').replace(/,/g, "'") + 'k';
  const dismissKiBanner = () => setState(s => ({ ...s, ki_metadata: { ...(s.ki_metadata || {}), bannerDismissed: true } }));

  // SLA + advisory option arrays with localised labels.
  // sla ist ein Array — gültige Zustände: [], ['basis'], ['weekend'], ['vip'], ['weekend','vip'].
  // Basis ist exklusiv zu Weekend/VIP; Weekend und VIP sind miteinander kombinierbar.
  const slaSelected = Array.isArray(p3.sla) ? p3.sla : (p3.sla ? [p3.sla] : []);
  const slaOptions = ['none','basis','weekend','vip'].map(id => ({
    id,
    label: t(`p3.sla_options.${id}.label`),
    sub:   t(`p3.sla_options.${id}.sub`),
    fee: { none: 0, basis: 1500, weekend: 7500, vip: 7500 }[id],
  }));
  const isSlaSelected = (id) => id === 'none'
    ? slaSelected.length === 0
    : slaSelected.includes(id);
  const onSlaClick = (id) => {
    if (id === 'none')  return setP3({ sla: [] });
    if (id === 'basis') return setP3({ sla: slaSelected.includes('basis') ? [] : ['basis'] });
    // weekend / vip: gegenseitig kombinierbar, ersetzen aber Basis
    const next = new Set(slaSelected.filter(x => x !== 'basis'));
    if (next.has(id)) next.delete(id); else next.add(id);
    setP3({ sla: Array.from(next) });
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <KiBanner state={state} onDismiss={dismissKiBanner}/>
      <header style={{ marginBottom: 24 }}>
        <span className="block__cat">{t('p3.section_label')}</span>
        <h1 className="block__title" style={{ fontSize: 24, marginTop: 6 }}>{t('p3.headline')}</h1>
      </header>

      {/* Build budget */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p3.block_build_cat')}</span>
            <h2 className="block__title">{t('p3.block_build_title')}</h2>
          </div>
        </div>
        <div className="cost-card">
          <div className="between" style={{ alignItems: 'flex-start' }}>
            <div>
              <span className="cost-card__cat">{t('p3.build_budget_cat')}</span>
              <div className="cost-card__amount" style={{ fontSize: 28 }}>{fmtChf(currentBudget)}</div>
              <div className="cost-card__rate">
                {t('p3.build_budget_rate', { hours: fmtNum(currentBudget / RATE_HOUR), pt: fmtNum(currentBudget / RATE_PT) })}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Pill tone="primary">{t('p3.build_budget_recommendation_pill', { amount: fmtChf(recommended) })}</Pill>
              {p3.budget != null && (
                <div style={{ marginTop: 8 }}>
                  <Btn kind="link" size="sm" onClick={() => setP3({ budget: null })}>{t('actions.back_to_recommendation')}</Btn>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <Slider
              value={currentBudget}
              onChange={(v) => setP3({ budget: v })}
              min={sliderMin} max={sliderMax} step={5000}
              format={(v) => fmtKchf(v)}
              recommended={recommended}
              recommendedLabel={t('p3.build_budget_recommendation_pill', { amount: '' }).replace(': ', '').trim()}
            />
          </div>

          {p3.budget != null && Math.abs(p3.budget - recommended) / recommended > 0.15 && (
            <div style={{ marginTop: 16 }}>
              {p3.budget < recommended * 0.85 ? (
                <Banner tone="warning" title={t('p3.budget_warn_title')}>
                  {t('p3.budget_warn_body', { pct: fmtPct((1 - p3.budget / recommended) * 100) })}
                </Banner>
              ) : (
                <Banner tone="info" title={t('p3.budget_comfort_title')}>
                  {t('p3.budget_comfort_body', { pct: fmtPct((p3.budget / recommended - 1) * 100) })}
                </Banner>
              )}
            </div>
          )}
        </div>

        <div className="grid-2" style={{ marginTop: 16 }}>
          <Field label={t('p3.downpayment_label')} hint={t('p3.downpayment_hint')}>
            <Slider value={p3.downPayment} onChange={(v) => setP3({ downPayment: v })} min={35} max={100} step={5} format={(v) => v + '%'}/>
          </Field>
          <Field
            label={t('p3.financing_label')}
            hint={p3.downPayment >= 100 ? t('p3.financing_hint_full') : t('p3.financing_hint_normal')}
          >
            <Slider
              value={p3.financingYears}
              onChange={(v) => setP3({ financingYears: v })}
              min={1} max={5} step={1}
              format={(v) => t(v === 1 ? 'p3.financing_year_one' : 'p3.financing_year_more', { value: v })}
              disabled={p3.downPayment >= 100}
            />
          </Field>
        </div>
      </div>

      {/* Operating costs */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p3.block_run_cat')}</span>
            <h2 className="block__title">{t('p3.block_run_title')}</h2>
          </div>
        </div>

        <div className="grid-3">
          <div className="cost-card">
            <span className="cost-card__cat">{t('p3.card_license_cat')}</span>
            <div className="cost-card__title">{t('p3.card_license_title')}</div>
            <div className="cost-card__rate">{t('p3.card_license_rate')}</div>
            <div className="cost-card__body">{t('p3.card_license_body')}</div>
            <div className="cost-card__amount">{fmtChf(r.yearlyLicense)}</div>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('license')}>{t('actions.what_included')}</Btn>
          </div>

          <div className="cost-card">
            <span className="cost-card__cat">{t('p3.card_service_cat')}</span>
            <div className="cost-card__title">{t('p3.card_service_title')}</div>
            <div className="cost-card__rate">{t('p3.card_service_rate')}</div>
            <div className="cost-card__body">{t('p3.card_service_body')}</div>
            <div className="cost-card__amount">{fmtChf(r.yearlyService)}</div>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('service')}>{t('actions.what_included')}</Btn>
          </div>

          <div className="cost-card">
            <span className="cost-card__cat">{t('p3.card_hosting_cat')}</span>
            <div className="cost-card__title">{t('p3.card_hosting_title')}</div>
            <div className="cost-card__rate">{t('p3.card_hosting_rate')}</div>
            <div className="cost-card__body">{t('p3.card_hosting_body')}</div>
            <div className="cost-card__amount">{fmtChf(r.yearlyHosting)}</div>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('hosting')}>{t('actions.what_included')}</Btn>
          </div>
        </div>

        <div className="cost-card" style={{ marginTop: 16 }}>
          <div className="between">
            <div>
              <span className="cost-card__cat">{t('p3.card_growth_cat')}</span>
              <div className="cost-card__title">{t('p3.card_growth_title')}</div>
              <div className="cost-card__rate">{t('p3.card_growth_rate')}</div>
            </div>
            <div className="cost-card__amount">{fmtChf(r.yearlyGrowth)}</div>
          </div>
          <p className="cost-card__body" style={{ marginTop: 8 }}>{t('p3.card_growth_body')}</p>
          <div style={{ marginTop: 8 }}>
            <Slider value={p3.growthBudget} onChange={(v) => setP3({ growthBudget: v })}
              min={CFG.weiterentwicklung.minProzent} max={CFG.weiterentwicklung.maxProzent} step={5}
              format={(v) => t('p3.growth_slider_format', { pct: v, amount: fmtChf(currentBudget * v / 100) })}/>
          </div>
        </div>
      </div>

      {/* Year overview */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p3.block_overview_cat')}</span>
            <h2 className="block__title">{t('p3.block_overview_title')}</h2>
          </div>
          <div className="segmented year-toggle" data-size="sm" style={{ width: 'auto', minWidth: 220 }}>
            <button className={`segmented__seg ${tableMode === 'table' ? 'is-active' : ''}`} onClick={() => setTableMode('table')}>
              <span>{t('p3.table_view')}</span>
            </button>
            <button className={`segmented__seg ${tableMode === 'chart' ? 'is-active' : ''}`} onClick={() => setTableMode('chart')}>
              <span>{t('p3.chart_view')}</span>
            </button>
          </div>
        </div>

        {tableMode === 'table' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="year-table">
              <thead>
                <tr>
                  <th>{t('p3.table_pos_header')}</th>
                  {r.years.map((y) => (
                    <th key={y.year} style={{ textAlign: 'right' }}>
                      {t('p3.table_year', { year: y.year })}{y.year === 0 ? <><br/><small style={{ fontWeight: 400, color: 'var(--text-color-muted)' }}>{t('p3.table_year_build')}</small></> : null}
                    </th>
                  ))}
                  <th style={{ textAlign: 'right' }}>{t('p3.table_sum_header')}</th>
                </tr>
              </thead>
              <tbody>
                {['down','buildRest','lic','serv','host','ops','grow'].map((key) => {
                  const sum = r.years.reduce((s, y) => s + (y[key] || 0), 0);
                  if (!sum) return null;
                  return (
                    <tr key={key}>
                      <td>{t(`p3.table_pos.${key}`)}</td>
                      {r.years.map((y) => (
                        <td key={y.year} className={y[key] ? '' : 'zero'} style={{ textAlign: 'right' }}>
                          {y[key] ? fmtChfTight(y[key]) : '—'}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right' }}><strong>{fmtChfTight(sum)}</strong></td>
                    </tr>
                  );
                })}
                <tr className="is-total">
                  <td>{t('p3.table_total_per_year')}</td>
                  {r.years.map((y) => (
                    <td key={y.year} style={{ textAlign: 'right' }}><strong>{fmtChfTight(y.total)}</strong></td>
                  ))}
                  <td style={{ textAlign: 'right' }}><strong>{fmtChfTight(r.tco5)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {tableMode === 'chart' && (
          <div className="cost-card" style={{ padding: '24px 28px' }}>
            <LineChart years={r.years}/>
          </div>
        )}

        <div className="grid-3" style={{ marginTop: 16 }}>
          <div className="kpi">
            <span className="kpi__label">{t('p3.kpi_build_label')}</span>
            <span className="kpi__value">{fmtChf(currentBudget)}</span>
            <span className="kpi__sub">{t('p3.kpi_build_sub')}</span>
          </div>
          <div className="kpi">
            <span className="kpi__label">{t('p3.kpi_tco_3_label')}</span>
            <span className="kpi__value">{fmtChf(r.tco3)}</span>
            <span className="kpi__sub">{t('p3.kpi_tco_3_sub')}</span>
          </div>
          <div className="kpi">
            <span className="kpi__label">{t('p3.kpi_tco_5_label')}</span>
            <span className="kpi__value">{fmtChf(r.tco5)}</span>
            <span className="kpi__sub">{t('p3.kpi_tco_5_sub')}</span>
          </div>
        </div>
      </div>

      {/* Service-Optionen Betrieb */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p3.block_service_cat')}</span>
            <h2 className="block__title">{t('p3.block_service_title')}</h2>
            <p className="block__lead">{t('p3.block_service_lead')}</p>
          </div>
        </div>

        {/* SLA */}
        <div className="cost-card">
          <div className="between" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <span className="cost-card__cat">{t('p3.sla_cat')}</span>
              <div className="cost-card__title">{t('p3.sla_title')}</div>
              <div className="cost-card__rate">{t('p3.sla_rate')}</div>
            </div>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('sla')}>
              {t('actions.what_included')}
            </Btn>
          </div>
          <div className="cardgrid" data-cols="4" style={{ marginTop: 8 }}>
            {slaOptions.map((opt) => (
              <OptCard
                key={opt.id}
                radio={opt.id === 'none' || opt.id === 'basis'}
                title={opt.label}
                sub={opt.sub}
                badge={<span className="optcard__price">{opt.fee
                  ? t('p3.sla_price_per_year', { amount: opt.fee.toLocaleString('de-CH').replace(/,/g, "'") })
                  : t('p3.sla_price_free')}</span>}
                selected={isSlaSelected(opt.id)}
                onClick={() => onSlaClick(opt.id)}
              />
            ))}
          </div>
        </div>

        {/* SEO + Datenschutz-Beratung — side by side */}
        <div className="grid-2" style={{ marginTop: 16 }}>
          <div className="cost-card">
            <div className="between" style={{ alignItems: 'flex-start' }}>
              <div>
                <span className="cost-card__cat">{t('p3.seo_cat')}</span>
                <div className="cost-card__title">{t('p3.seo_title')}</div>
                <div className="cost-card__rate">{t('p3.seo_rate')}</div>
              </div>
              <ToggleSwitch
                checked={!!p3.seoChecks}
                onChange={(on) => setP3({ seoChecks: on })}
                aria-label={t('p3.seo_aria')}
              />
            </div>
            <p className="cost-card__body" style={{ marginTop: 8 }}>{t('p3.seo_body')}</p>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('seo-checks')}>
              {t('actions.what_included')}
            </Btn>
          </div>

          <div className="cost-card">
            <div className="between" style={{ alignItems: 'flex-start' }}>
              <div>
                <span className="cost-card__cat">{t('p3.dataprotection_cat')}</span>
                <div className="cost-card__title">{t('p3.dataprotection_title')}</div>
                <div className="cost-card__rate">{t('p3.dataprotection_rate')}</div>
              </div>
              <ToggleSwitch
                checked={!!p3.dataProtection}
                onChange={(on) => setP3({ dataProtection: on })}
                aria-label={t('p3.dataprotection_aria')}
              />
            </div>
            <p className="cost-card__body" style={{ marginTop: 8 }}>{t('p3.dataprotection_body')}</p>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('data-protection')}>
              {t('actions.what_included')}
            </Btn>
          </div>
        </div>

        {/* Security Scanning — 4 packages (none | yearly | quarterly | monthly) */}
        <div className="cost-card" style={{ marginTop: 16 }}>
          <div className="between" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <span className="cost-card__cat">
                {t('p3.secscan_cat')}
                <span className="advisory-card__badge"><i className="fa-solid fa-star"/>{t('p3.advisory_recommended')}</span>
              </span>
              <div className="cost-card__title">{t('p3.secscan_title')}</div>
              <div className="cost-card__rate">{t('p3.secscan_rate')}</div>
            </div>
            <Btn kind="link" size="sm" iconRight="arrow-right" onClick={() => openDrawer('security-scanning')}>
              {t('actions.what_included')}
            </Btn>
          </div>
          <p className="cost-card__body" style={{ marginTop: 0, marginBottom: 12 }}>{t('p3.secscan_body')}</p>
          <div className="cardgrid" data-cols="4">
            {['none','yearly','quarterly','monthly'].map((id) => {
              const fee = CFG.serviceOptionen.securityScanning.pakete[id] || 0;
              const current = String(p3.securityScanning || 'none');
              return (
                <OptCard
                  key={id}
                  radio
                  title={t(`p3.secscan_options.${id}.label`)}
                  sub={t(`p3.secscan_options.${id}.sub`)}
                  badge={<span className="optcard__price">{fee
                    ? t('p3.sla_price_per_year', { amount: fee.toLocaleString('de-CH').replace(/,/g, "'") })
                    : t('p3.sla_price_free')}</span>}
                  selected={current === id}
                  onClick={() => setP3({ securityScanning: id })}
                />
              );
            })}
          </div>
        </div>

        {(r.yearlyOps > 0) && (
          <div className="cost-card cost-card--sum" style={{ marginTop: 16 }}>
            <div className="between">
              <div>
                <span className="cost-card__cat">{t('p3.service_sum_cat')}</span>
                <div className="cost-card__rate">{t('p3.service_sum_rate')}</div>
              </div>
              <div className="cost-card__amount">{fmtChf(r.yearlyOps)} <small style={{ fontWeight: 400, color: 'var(--text-color-muted)' }}>/ {t('common.currency_chf') === 'CHF' ? 'Jahr' : 'year'}</small></div>
            </div>
          </div>
        )}
      </div>

      {/* Beratungsoptionen sind auf die Übersichts-Seite verschoben — Beratung
          ist optional und projektunabhängig, daher gehört sie nicht in den TCO. */}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Toggle switch (used inline in Service-Optionen)
// -----------------------------------------------------------------------------
const ToggleSwitch = ({ checked, onChange, ...rest }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className={`toggle-switch ${checked ? 'is-on' : ''}`}
    onClick={() => onChange(!checked)}
    {...rest}
  >
    <span className="toggle-switch__thumb"/>
  </button>
);

// -----------------------------------------------------------------------------
// Advisory card with S/M/L sizing inside
// -----------------------------------------------------------------------------
const AdvisoryCard = ({ recommended, cat, title, lead, value, onChange, onDetails, sizes }) => {
  // Default sizes (design / spec): 5 / 10 / 20 PT with generic sub-labels.
  // Callers can pass `sizes` to override PT values and sub-labels per package
  // (e.g. the Prototyping card uses 10 / 20 / 30 with package-specific names).
  const finalSizes = sizes || [
    { id: 'none', label: t('p3.advisory_size_none'),         pt: 0,  sub: t('p3.advisory_size.none.sub') },
    { id: 's',    label: t('p3.advisory_size.s.label'),      pt: 5,  sub: t('p3.advisory_size.s.sub') },
    { id: 'm',    label: t('p3.advisory_size.m.label'),      pt: 10, sub: t('p3.advisory_size.m.sub') },
    { id: 'l',    label: t('p3.advisory_size.l.label'),      pt: 20, sub: t('p3.advisory_size.l.sub') },
  ];
  return (
    <div className={`cost-card advisory-card ${recommended ? 'is-recommended' : ''}`} style={{ marginTop: 16 }}>
      <div className="between" style={{ alignItems: 'flex-start' }}>
        <div>
          {cat && (
            <span className="cost-card__cat">
              {cat}
              {recommended && <span className="advisory-card__badge"><i className="fa-solid fa-star"/>{t('p3.advisory_recommended')}</span>}
            </span>
          )}
          <div className="cost-card__title">{title}</div>
          <p className="cost-card__body" style={{ marginTop: 4 }}>{lead}</p>
        </div>
        <Btn kind="link" size="sm" iconRight="arrow-right" onClick={onDetails}>
          {t('actions.what_included')}
        </Btn>
      </div>
      <div className="cardgrid" data-cols="4" style={{ marginTop: 12 }}>
        {finalSizes.map((s) => (
          <OptCard
            key={s.id}
            radio
            title={s.id === 'none' ? s.label : t('p3.advisory_size_pt_format', { label: s.label, pt: s.pt })}
            sub={s.sub}
            badge={<span className="optcard__price">{s.pt ? `CHF ${(s.pt * 1440).toLocaleString('de-CH').replace(/,/g, "'")}` : '—'}</span>}
            selected={value === s.id}
            onClick={() => onChange(s.id)}
          />
        ))}
      </div>
    </div>
  );
};

export { Page3, AdvisoryCard };
